import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useCareTeamMember from '../../../../../hooks/api/useCareTeamMember';
import CareTeamMemberHeader from '../../../../shared/CareTeam/CareTeamMemberHeader';
import { getIntl } from '../../../../../utils/intl';
import addCountToString from '../../../../../utils/addCountToString';
import { useHasPermission, usePermittedDomains } from '../../../../v2BaseComponents/Permissions/hooks';
import useNotes from '../../../../../hooks/api/useNotes';
import { TabView, TabOption } from '../../../../v2BaseComponents/TabView';
import NotesList from '../../../../shared/Notes/NotesList';
import { relatedResourcesMap } from './relatedResourcesMap';
import { categoryToDomain, domainToCategory } from '../../../../../utils/resourceUtils/medicalDataPermissions';
import useCareTeamMemberResources from '../../../../../hooks/api/useCareTeamMemberResources';
import { isPresent } from 'ts-is-present';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import CareTeamMemberResources from './CareTeamMemberResources';
import DetailsTab from './DetailsTab';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';

const intl = getIntl('CareTeamMemberDetails', {
    edit: 'Edit',
    details: 'Details',
    notes: 'Notes',
    duplicateCareTeamMember: 'Duplicate',
    deleteCareTeamMember: 'Delete Care Team Member',
    inviteCollaborator: 'Invite a Collaborator',
    condition: 'Conditions ({count})',
    medication: 'Medications ({count})',
    allergy: 'Allergies ({count})',
    visit: 'Visits ({count})',
    vaccination: 'Vaccinations ({count})',
    procedure: 'Procedures ({count})',
    imaging: 'Imaging ({count})',
    lab: 'Labs ({count})',
});

type Tab = 'details' | 'notes' | MedicalDataTypes.PrimaryRecordResourceCategory;

export type CareTeamMemberScreenOwnProps = {};

export type CareTeamMemberScreenProps = {
    route: RouteProp<ProfilesTabParamList, 'care-team-member'>;
};

export type CareTeamMemberDetailsProps = CareTeamMemberScreenOwnProps & CareTeamMemberScreenProps;

const CareTeamMemberScreen: React.FC<CareTeamMemberDetailsProps> = (props) => {
    const [tab, setTab] = React.useState<Tab>('details');
    const profileNavigator = useProfilesTabNavigation();
    const { profileId, careTeamMemberId } = props.route.params;
    const { isLoadingCareTeamMember, careTeamMember } = useCareTeamMember({
        medicalProfileId: profileId,
        careTeamMemberId: careTeamMemberId,
    });
    const allRelatedResourceCategories = careTeamMember ? relatedResourcesMap[careTeamMember.careTeamMemberType] : [];
    const permittedViewResourceCategories = usePermittedDomains(
        profileId,
        allRelatedResourceCategories.map((category) => categoryToDomain(category)),
        'view',
    )
        .map(domainToCategory)
        .filter(isPresent);
    const permittedEditResourceCategories = usePermittedDomains(
        profileId,
        allRelatedResourceCategories.map((category) => categoryToDomain(category)),
        'edit',
    )
        .map(domainToCategory)
        .filter(isPresent);
    const { isLoadingResources, resources } = useCareTeamMemberResources({
        medicalProfileId: profileId,
        careTeamMemberId: careTeamMemberId,
        resourceCategories: permittedViewResourceCategories,
    });
    const hasCareTeamEditPermission = useHasPermission(profileId, 'careTeam', 'edit');
    const noteQueryArgs = {
        medicalProfileId: profileId,
        entityType: 'care-team-member',
        entityId: careTeamMemberId,
    } as const;
    const { notes } = useNotes({
        noteQueryArgs,
    });

    const getCategoryTabText = (category: MedicalDataTypes.PrimaryRecordResourceCategory) => {
        const count = isLoadingResources ? '...' : resources[category]?.length || 0;
        return intl(category, { count });
    };
    const tabOptions: TabOption[] = [
        {
            title: intl('details'),
            value: 'details',
            renderTabContent: () => <DetailsTab careTeamMemberId={careTeamMemberId} profileId={profileId} />,
        },
        {
            title: addCountToString(intl('notes'), notes.notes?.length || 0),
            value: 'notes',
            renderTabContent: () => (
                <NotesList
                    linkEmptyStateHint
                    noteQueryArgs={noteQueryArgs}
                    onAddNote={(destination) => {
                        profileNavigator.navigate('add-note', destination);
                    }}
                    onViewDetails={(note) => {
                        profileNavigator.navigate('note', {
                            noteId: note.noteId,
                            noteEntityType: noteQueryArgs.entityType,
                            profileId: profileId,
                        });
                    }}
                />
            ),
        },
        ...permittedViewResourceCategories.map((category) => ({
            title: getCategoryTabText(category),
            value: category,
            renderTabContent: () => (
                <CareTeamMemberResources
                    profileId={profileId}
                    resourceCategory={category}
                    careTeamMemberId={careTeamMemberId}
                    relatedResources={resources[category] || []}
                    isLoadingRelatedResources={isLoadingResources}
                    hasEditPermission={permittedEditResourceCategories.includes(category) && hasCareTeamEditPermission}
                />
            ),
        })),
    ];

    return (
        <AnimateLoadedContent
            key={props.route.params.careTeamMemberId}
            isLoading={isLoadingCareTeamMember || !careTeamMember}
        >
            {() => (
                <>
                    <HeaderContainer>
                        <CareTeamMemberHeader
                            careTeamMember={careTeamMember!}
                            source={{
                                source: 'profile',
                                medicalProfileId: profileId,
                            }}
                            careTeamMemberId={careTeamMemberId}
                        />
                    </HeaderContainer>
                    <TabView selectedTab={tab} tabs={tabOptions} onChange={(_) => setTab(_ as Tab)} />
                </>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(CareTeamMemberScreen);
